import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
    sliderContainer,
    sliderContent,
    slide,
    navBtn,
    prev,
    next,
    padlock
} from "./image-slider.module.scss";
import { IProduct } from "../../models/product.model";
import useUser from "../../apps/auth/hooks/use-user";
import { useFormikContext } from "formik";
import { IGeneratorFormValues, TBlockedProduct } from "../../models/generator.model";
import { postProductStatistic} from '../../services/product-services';
import Padlock from "../molecules/padlock";

interface ImageSliderProps {
    products: IProduct[];
    onSlideUp?: () => void;
    surveyPopup: boolean;
    isLoggedIn: boolean;
    customIndex?: number;
}

interface SlideProps {
    product: IProduct;
    index: number;
    currentIndex: number;
    isLocked: boolean;
    onPadlockClick?: () => void;
    isLoggedIn: boolean;

}

const handleLinkClick = (event: React.MouseEvent<HTMLDivElement>, product: IProduct) => {
    console.log('handleLinkClick', event);
    console.log('Clicked on a div element');
    postProductStatistic(product);
    const url = product.affiliateUrl || product.url;
    if (url) {
        window.open(url, '_blank');
    }
}

const Slide: React.FC<SlideProps> = React.memo(
    ({ product, index, currentIndex, isLocked, onPadlockClick, isLoggedIn }) => (
        <div
            onClick={(event) => handleLinkClick(event, product)}
            className={`${slide} ${index === currentIndex ? "active" : ""}`}
            style={{ transform: `translateX(-${currentIndex * 100}%)`, backgroundImage: `url("${product.imgUrl}")` }}
        >
            {onPadlockClick && isLoggedIn && (
                <Padlock
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onPadlockClick();
                    }}
                    className={padlock}
                    isLocked={isLocked}
                />
            )}
            {/* <img src={src} alt={`Slide ${index}`} /> */}
        </div>
    )
);
const ImageSlider: React.FC<ImageSliderProps> = ({ products, onSlideUp, surveyPopup, isLoggedIn, customIndex }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const sliderRef = useRef<HTMLDivElement>(null);
    const formik = useFormikContext<IGeneratorFormValues>();

    const lockedStatus = (product: IProduct) => getIsLocked(formik.values.blockedProducts, product);
    
    const sortedProducts = useMemo(() => {
        return [...products].sort((a, b) => Number(lockedStatus(a)) - Number(lockedStatus(b)));
        // Jeśli chcesz sortować tylko raz (na podstawie początkowego stanu), użyj po prostu [...products]
    }, [products]); // <-- tylko products!
    
    const nextSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev === products.length - 1 ? 0 : prev + 1));
    }, [products.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? products.length - 1 : prev - 1));
    }, [products.length]);

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!sliderRef.current) return;

        const touchCurrentY = e.touches[0].clientY;
        const deltaY = touchStartY.current - touchCurrentY;

        // Blokuj przewijanie tylko gdy przesunięcie jest głównie pionowe
        if (Math.abs(deltaY) > 10) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const deltaX = touchStartX.current - touchEndX;
        const deltaY = touchStartY.current - touchEndY;

        if (deltaY > 50 && onSlideUp) {
            console.log("generate new");
            if (surveyPopup) {
                return;
            }
            onSlideUp();
        } else if (Math.abs(deltaX) > Math.abs(deltaY)) {
            // Wykonuj przesuwanie w poziomie tylko jeśli ruch był bardziej poziomy niż pionowy
            if (deltaX > 50) {
                console.log("Swiped Left");
                prevSlide();
            } else if (deltaX < -50) {
                console.log("Swiped Right");
                nextSlide();
            }
        }
    };

    const handleLockProduct = (product: IProduct) => () => {
        const lockedProducts = formik.values.blockedProducts;
        const lockedProductIds = lockedProducts.map((product) => product.productId);
        let newLockedProducts: TBlockedProduct[];
        if (lockedProductIds.includes(product.productId)) {
            newLockedProducts = lockedProducts.filter(
                (lockedProduct) => lockedProduct.productId !== product.productId
            );
        } else {
            newLockedProducts = [
                ...lockedProducts,
                { productId: product.productId, categoryGroup: product.categoryGroup },
            ];
        }
        formik.setFieldValue("blockedProducts", newLockedProducts);
    };
    useEffect(() => {
        const currentSliderRef = sliderRef.current;
        if (currentSliderRef) {
            currentSliderRef.addEventListener('touchmove', handleTouchMove as any, { passive: false });
        }
        return () => {
            if (currentSliderRef) {
                currentSliderRef.removeEventListener('touchmove', handleTouchMove as any);
            }
        };
    }, []);


    useEffect(() => {
        if (typeof customIndex === 'number' && customIndex >= 0 && customIndex < products.length) {
            setCurrentIndex(customIndex);
        }
    }, [customIndex]);


    return (
        <div
            ref={sliderRef}
            className={sliderContainer}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div className={sliderContent}>
                {sortedProducts.map((product, index) => (
                    <Slide
                        key={index}
                        product={product}
                        index={index}
                        isLoggedIn={isLoggedIn}
                        currentIndex={currentIndex}
                        onPadlockClick={handleLockProduct(product)}
                        isLocked={getIsLocked(formik.values.blockedProducts, product)}
                    />
                ))}
            </div>

            <button type="button" className={`${navBtn} ${prev}`} onClick={prevSlide}>
                &#10094;
            </button>
            <button type="button" className={`${navBtn} ${next}`} onClick={nextSlide}>
                &#10095;
            </button>
        </div>
    );
};

function getIsLocked(blockedProducts: TBlockedProduct[], product: IProduct) {
    return blockedProducts.map((product) => product.productId).includes(product.productId);
}

export default ImageSlider;
